/**
 * WizardPhotoBlock — Foto-Block mit nativer Handy-Kamera + KI-Vorschlag.
 * ======================================================================
 * Iter89z-wiz (2026-02-26).
 *
 * Erweiterung des `VCheckPhotoBlock`:
 * - `capture="environment"` am File-Input öffnet direkt die Rückkamera
 *   auf dem Handy (Chrome Android / Safari iOS)
 * - "KI analysieren"-Button nach dem Upload für Fotoplätze, deren Schema
 *   `ai_vision` definiert (nameplate / pipe_dimension / fuse_box /
 *   door_measurement). Zeigt die Vorschläge als Chip mit "Übernehmen" /
 *   "Verwerfen".
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Trash2, Camera, Image as ImageIcon, Sparkles, Check, X, Download, Pencil, Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { isOfflineError } from "@/lib/vcheckOffline";


export default function WizardPhotoBlock({
  field, photos, projectId, onChanged, onApplySuggestions, missing, offline,
  embedded = false, minPhotos, sketches = [],
}) {
  const navigate = useNavigate();
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);        // photo_id while analyzing
  const [suggestionChip, setSuggestionChip] = useState(null); // { photoId, suggestions }
  const inputRef = useRef(null);

  const min = minPhotos || field.min_photos || 1;
  const arr = photos || [];
  const have = arr.length + sketches.length;
  const hasAi = !!field.ai_vision;

  const onFiles = async (files) => {
    setUploading(true);
    let okCount = 0, offlineCount = 0, failCount = 0;
    let lastPhotoId = null;
    try {
      for (const f of files) {
        try {
          const form = new FormData();
          form.append("file", f);
          const r = await api.post(
            `/projects/${projectId}/vcheck-wp/photos/${field.key}/upload`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          lastPhotoId = r.data?.photo?.id || lastPhotoId;
          okCount += 1;
        } catch (e) {
          if (offline && isOfflineError(e)) {
            await offline.enqueuePhoto(field.key, f);
            offlineCount += 1;
          } else {
            failCount += 1;
            toast.error(e.response?.data?.detail || "Foto-Upload fehlgeschlagen");
          }
        }
      }
      if (okCount > 0) toast.success(`${okCount} Foto${okCount === 1 ? "" : "s"} hochgeladen`);
      if (offlineCount > 0) toast.info(`${offlineCount} Foto${offlineCount === 1 ? "" : "s"} offline gespeichert — Sync bei Verbindung`);
      if (okCount > 0) onChanged?.();
    } finally {
      setUploading(false);
    }
    // KI-Auswertung startet automatisch für das zuletzt hochgeladene Foto
    if (hasAi && lastPhotoId) analyze(lastPhotoId, true);
  };

  const remove = async (photoId) => {
    if (!window.confirm("Foto wirklich löschen?")) return;
    try {
      await api.post(`/projects/${projectId}/vcheck-wp/photos/${field.key}/delete`, { photo_id: photoId });
      toast.success("Foto gelöscht");
      onChanged?.();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const analyze = async (photoId, auto = false) => {
    setAnalyzing(photoId);
    setSuggestionChip(null);
    try {
      const r = await api.post(
        `/projects/${projectId}/vcheck-wp/photos/${field.key}/analyze`,
        { photo_id: photoId },
      );
      const sugg = r.data?.suggestions || {};
      const hasValues = Object.values(sugg).some((v) => v !== null && v !== undefined && v !== "");
      if (!hasValues) {
        if (!auto) toast.info("KI konnte keine klaren Angaben erkennen");
        return;
      }
      setSuggestionChip({ photoId, suggestions: sugg });
    } catch (e) {
      if (!auto) toast.error(e.response?.data?.detail || "KI-Analyse fehlgeschlagen");
    } finally {
      setAnalyzing(null);
    }
  };

  const applyAndClose = () => {
    if (!suggestionChip) return;
    onApplySuggestions?.(suggestionChip.suggestions);
    setSuggestionChip(null);
  };

  const discard = () => setSuggestionChip(null);

  const colorClass = embedded ? "" : `rounded-lg border p-3 ${have >= min
    ? "bg-emerald-500/[0.04] border-emerald-500/25"
    : missing ? "bg-red-500/[0.06] border-red-500/40"
              : "bg-yellow-500/[0.05] border-yellow-500/25"}`;

  return (
    <div className={`space-y-2.5 ${colorClass}`}
         data-testid={`vcheck-photo-${field.key}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-zinc-100">
            {!embedded && <><ImageIcon size={13} /> {field.label}</>}
            <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
              have >= min ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/15 text-red-300"
            }`}>
              {have} / {min} {field.floorplan ? "Plan/Skizze" : "Fotos"}
            </span>
            {hasAi && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-200 border border-amber-400/40 uppercase">
                <Sparkles size={9} /> KI
              </span>
            )}
          </div>
          {field.hint && (
            <div className="text-[12.5px] text-zinc-400 mt-1">{field.hint}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`vcheck-photo-${field.key}-upload`}
          className="h-11 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[13px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
          Jetzt fotografieren
        </button>
        {field.floorplan && (
          <div className="flex gap-2 w-full">
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploading}
                    data-testid={`vcheck-photo-${field.key}-gallery`}
                    className="flex-1 h-11 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Upload size={13} /> Plan hochladen
            </button>
            <button type="button"
                    onClick={() => navigate(`/projects/${projectId}/sketch?category=Grundriss&return=tech`)}
                    data-testid={`vcheck-photo-${field.key}-sketch`}
                    className="flex-1 h-11 px-3 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/40 text-sky-200 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5">
              <Pencil size={13} /> Skizze zeichnen
            </button>
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
                   onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }}
        />
      </div>

      {/* Thumbnails + KI-Analyse-Button pro Foto */}
      {(arr.length > 0 || sketches.length > 0) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {sketches.map((sk) => (
            <div key={sk.id} className="relative rounded-lg overflow-hidden bg-zinc-900 border border-sky-500/40" data-testid={`vcheck-floorplan-sketch-${sk.id}`}>
              <img src={fileUrl(sk.storage_path)} alt={sk.title} className="w-24 h-24 sm:w-20 sm:h-20 object-cover" loading="lazy" />
              <span className="absolute bottom-0.5 left-0.5 bg-sky-500/90 text-zinc-950 rounded px-1 text-[10px] font-bold">Skizze</span>
            </div>
          ))}
          {arr.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
              <img src={fileUrl(p.path)} alt={p.name}
                   className="w-24 h-24 sm:w-20 sm:h-20 object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => remove(p.id)}
                data-testid={`vcheck-photo-del-${p.id}`}
                className="absolute top-0.5 right-0.5 bg-red-500/90 hover:bg-red-500 text-white rounded p-1"
                title="Löschen"
              >
                <Trash2 size={11} />
              </button>
              {hasAi && (
                <button
                  type="button"
                  onClick={() => analyze(p.id)}
                  disabled={analyzing === p.id}
                  data-testid={`vcheck-photo-analyze-${p.id}`}
                  className="absolute bottom-0.5 left-0.5 bg-amber-400/95 hover:bg-amber-400 text-zinc-950 rounded px-1.5 py-0.5 text-[10px] font-bold inline-flex items-center gap-0.5 disabled:opacity-50"
                  title="Mit KI auswerten"
                >
                  {analyzing === p.id
                    ? <Loader2 size={9} className="animate-spin" />
                    : <Sparkles size={9} />}
                  KI
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {analyzing && (
        <div className="rounded-lg border border-amber-400/50 bg-amber-500/10 px-3 py-2 text-[12px] font-semibold text-amber-200 inline-flex items-center gap-1.5"
             data-testid="vcheck-ai-analyzing">
          <Loader2 size={13} className="animate-spin" /> KI wertet das Foto aus …
        </div>
      )}

      {/* KI-Vorschlags-Chip (zeigt Werte + Übernehmen/Verwerfen) */}
      {suggestionChip && (
        <div className="rounded-lg border-2 border-amber-400/60 bg-amber-500/10 p-3 space-y-2"
             data-testid="vcheck-ai-chip">
          <div className="flex items-center gap-1.5 text-[12px] font-bold text-amber-200">
            <Sparkles size={13} /> KI-Vorschlag — Darf ich das übernehmen?
          </div>
          <div className="text-[11.5px] text-zinc-200 space-y-0.5 font-mono bg-zinc-900/60 rounded px-2 py-1.5">
            {Object.entries(suggestionChip.suggestions).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <span className="text-zinc-400">{k}:</span>
                <span className="text-zinc-100 font-semibold text-right">
                  {v === null || v === undefined || v === ""
                    ? <em className="text-zinc-600">— nicht erkannt —</em>
                    : String(v)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyAndClose}
              data-testid="vcheck-ai-apply"
              className="h-10 flex-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5"
            >
              <Check size={13} /> Übernehmen
            </button>
            <button
              type="button"
              onClick={discard}
              data-testid="vcheck-ai-discard"
              className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
            >
              <X size={13} /> Verwerfen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
